import React, { isValidElement } from 'react';
import { Link } from 'react-router';
import { Parser } from 'html-to-react';

import { openPopup } from 'helpers';
import ProductUtils from 'helpers/ProductUtils';

const htmlToReactParser = new Parser();

const makeSizeChartLi = (testId, link, containerClass, index) => (
  <li key={index} className={containerClass} data-test-id={testId('sizeChartLi')}>
    {link}
  </li>
);

export const buildPopupProps = className => {
  const newProps = {};
  if (className && className.includes('popup')) {
    const [width, height] = className.split('-').slice(1);
    newProps.className = null;
    newProps.onClick = openPopup;
    newProps['data-popup-options'] = `width=${width},height=${height}`;
  }
  return newProps;
};

export const makeSizeChartLink = (parsedLink, sizeChartsPropTransformer) => {
  if (typeof parsedLink === 'string') {
    return parsedLink;
  }
  const { props: { className } } = parsedLink;
  const transformedProps = sizeChartsPropTransformer(parsedLink, buildPopupProps(className));
  return transformedProps ? React.cloneElement(parsedLink, transformedProps) : null;
};

const makeStandardSizeChartLink = parsedLink => makeSizeChartLink(parsedLink, (parsedLink, props) => props);
const makeSizeChartLinkWithMultipleElements = (elements, sizeChartLinkFactory) => React.Children.map(elements, sizeChartLinkFactory);

export const makeSizeChartLiBody = (sizeChart, multipleElementSizeChartLinkFactory = makeSizeChartLinkWithMultipleElements, sizeChartLinkFactory = makeStandardSizeChartLink) => {
  const trimmedSizeChart = sizeChart.trim();
  let parsedLink = htmlToReactParser.parse(trimmedSizeChart);
  if (isValidElement(parsedLink)) {
    return sizeChartLinkFactory(parsedLink);
  }

  parsedLink = htmlToReactParser.parse(`<span>${trimmedSizeChart}</span>`);
  if (!isValidElement(parsedLink)) {
    return null;
  }
  const { props: { children } } = parsedLink;
  if (children.length <= 1) {
    return parsedLink;
  }

  return multipleElementSizeChartLinkFactory(children, sizeChartLinkFactory);
};

export function makeSizeCharts(defaultProductType, { sizeCharts = [] }, containerClass, testId) {
  const isShoe = ProductUtils.isShoeType(defaultProductType);
  const hasSizeCharts = sizeCharts.length > 0;

  // if it's not a shoe and doesn't have size chart data...
  if (!isShoe && !hasSizeCharts) {
    // don't render a size chart
    return [];
  }

  // if it has one or more size charts (may or may not be a shoe)...
  if (hasSizeCharts) {
    // map each size chart to markup
    return sizeCharts.map((sizeChart, index) => {
      const link = makeSizeChartLiBody(sizeChart);
      return makeSizeChartLi(testId, link, containerClass, index);
    });
  }

  // otherwise return the default shoe size chart markup
  const link = (
    <Link
      to="/measure-your-shoe-size"
      onClick={openPopup}
      data-popup-options="width=1000,height=800"
      data-track-action="Product-Page"
      data-track-label="Tabs"
      data-track-value="Size-Chart"
      data-test-id={testId('descriptionSizeChart')}>
      View the size chart
    </Link>
  );
  return [makeSizeChartLi(testId, link, containerClass, 0)];
}
